/**
 * How long a game stays open for ratings and man-of-the-match votes.
 *
 * The clock starts when the game is logged, not when it was played — that's the
 * moment everyone's phone gets the result, and it's the only timestamp the app
 * actually records. Log a game a week late and the group still gets three full
 * days on it.
 *
 * Why close it at all: an average that can still move is not a result. Leaving
 * every game open forever means a player's rating from March quietly changes in
 * August because one person finally got round to it, and the History screen
 * accumulates an ever-growing pile of things you half-owe an opinion on. A
 * deadline turns rating into something you do, rather than something you are
 * always behind on.
 *
 * The real enforcement is in the database (sql/08). This module is what the
 * screen uses to say so before you tap.
 */
export const VOTING_WINDOW_HOURS = 72;

const HOUR = 3600 * 1000;

/** When a game stops accepting votes and ratings, or null if we can't tell. */
export function votingClosesAt(game) {
  const started = new Date(game?.created_at ?? NaN).getTime();
  if (!Number.isFinite(started)) return null;
  return new Date(started + VOTING_WINDOW_HOURS * HOUR);
}

/**
 * Rounded up, deliberately. With 30 minutes left "1 hour left" overstates it by
 * half an hour; "0 hours left" reads as closed when you can still vote. The
 * error that costs someone a vote is the worse one.
 */
function remainingLabel(ms) {
  // Days only above 48 hours. Below that, rounding up puts everything from 25
  // hours to 47 in the same "2 days left" bucket, which is a whole day of
  // ambiguity in a three-day window. "36 hours left" costs nothing to read and
  // means one thing.
  if (ms >= 48 * HOUR) {
    const days = Math.ceil(ms / (24 * HOUR));
    return `${days} day${days === 1 ? '' : 's'} left`;
  }
  if (ms < 60000) return 'closing now';
  if (ms >= HOUR) {
    const hours = Math.ceil(ms / HOUR);
    return `${hours} hour${hours === 1 ? '' : 's'} left`;
  }
  const mins = Math.ceil(ms / 60000);
  return `${mins} minute${mins === 1 ? '' : 's'} left`;
}

/**
 * `{ open, label, msLeft, closesAt }` for a game.
 *
 * `now` is a parameter so this is testable and so a screen can pass one clock to
 * every card rather than each card reading its own and disagreeing by a tick.
 */
export function votingState(game, now = Date.now()) {
  const closesAt = votingClosesAt(game);
  if (!closesAt) return { open: false, label: 'voting closed', msLeft: 0, closesAt: null };

  const msLeft = closesAt.getTime() - now;
  if (msLeft <= 0) return { open: false, label: 'voting closed', msLeft: 0, closesAt };
  return { open: true, label: remainingLabel(msLeft), msLeft, closesAt };
}

/** "Sun 14 Sep, 19:40" — for telling someone exactly when their chance ran out. */
export function closedOnText(closesAt) {
  if (!closesAt) return '';
  return closesAt.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
